import { PetiteAnnonce } from "../../lib/interfaces/petite-annonce.interface";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Button } from "../ui/button";
import { EyeIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { ksPetitesAnnoncesQK } from "../../lib/query-keys";
import { findPetiteAnnonceById } from "../../lib/queries/petites-annonces-queries";
import { useState } from "react";
import { Skeleton } from "../ui/skeleton";
import dayjs from "dayjs";
import { getCouleurPetiteAnnonce, getEtatPetiteAnnonce } from "../../lib/helpers/petites_annonces_helpers";

interface Props {
  initialPetiteAnnonce: PetiteAnnonce;
}

export default function ShowPetiteAnnonceButton({ initialPetiteAnnonce }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const { data: petiteAnnonce } = useQuery({
    queryKey: [ksPetitesAnnoncesQK, initialPetiteAnnonce.id],
    queryFn: () => findPetiteAnnonceById(initialPetiteAnnonce.id),
    enabled: isOpen,
    initialData: initialPetiteAnnonce,
    staleTime: 0,
  });

  return (
    <div>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button icon={<EyeIcon size={16} />} variant='default'>
            Consulter
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Petite annonce</DialogTitle>
            <DialogDescription>Retrouvez ici le contenu de votre petite annonce.</DialogDescription>
          </DialogHeader>
          <div className='text-sm'>
            {/* Etat de l'annonce */}
            <div className={`${getCouleurPetiteAnnonce(petiteAnnonce.etat)} px-2 py-1 rounded-md w-max`}>
              {getEtatPetiteAnnonce(petiteAnnonce.etat)}
            </div>

            {/* Catégorie */}
            <div className='flex flex-col mt-3'>
              <div className='font-bold'>Catégorie</div>
              <div className='text-gray-500'>{petiteAnnonce.categorie?.nom || "Chargement..."}</div>
            </div>

            {/* Contenu de l'annonce */}
            <div className='font-bold mt-4'>Contenu</div>
            <div className='text-sm text-gray-500'>{petiteAnnonce.ligne1}</div>
            {petiteAnnonce.ligne2 && <div className='text-sm text-gray-500'>{petiteAnnonce.ligne2}</div>}
            {petiteAnnonce.ligne3 && <div className='text-sm text-gray-500'>{petiteAnnonce.ligne3}</div>}
            {petiteAnnonce.ligne4 && <div className='text-sm text-gray-500'>{petiteAnnonce.ligne4}</div>}
            {petiteAnnonce.ligne5 && <div className='text-sm text-gray-500'>{petiteAnnonce.ligne5}</div>}
            {petiteAnnonce.ligne6 && <div className='text-sm text-gray-500'>{petiteAnnonce.ligne6}</div>}

            {/* Option tramage */}
            <div className='flex mt-3 gap-2 items-center'>
              <div className='font-bold'>Tramage :</div>
              <div className='text-gray-500'>{petiteAnnonce.tramage ? "Oui" : "Non"}</div>
            </div>

            {/* Mois de publication */}
            <div className='flex flex-col gap-0.5 mt-3'>
              <div className='font-bold'>Mois de publication</div>
              <div className='text-gray-500'>
                {petiteAnnonce.moisDeParution ? (
                  petiteAnnonce.moisDeParution.map(({ mois }) => (
                    <div key={mois}>- {dayjs(mois).format("MMMM YYYY")}</div>
                  ))
                ) : (
                  <div className='flex flex-col gap-0.5'>
                    <Skeleton className='w-28 h-5' />
                    <Skeleton className='w-28 h-5' />
                    <Skeleton className='w-28 h-5' />
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
